import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Clock, RefreshCw, Save, AlertTriangle, Search, Pencil, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  getAutomationTiming,
  putAutomationTiming,
  deleteAutomationTiming,
} from '../../services/api.admin';
import api from '../../services/api';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIGNAL_MODE_LABEL = {
  disabled: 'Disabled',
  bar_close: 'Bar Close',
  intrabar: 'Intrabar',
};

const SIGNAL_MODE_DESCRIPTION = {
  disabled:
    'Strategy alerts remain visible but automation does not act on them. Default for safety.',
  bar_close:
    'Automation evaluates only when a candle has fully closed. No repaint risk; one decision per bar.',
  intrabar:
    'Automation evaluates on every live quote (throttled). Fastest, but signals can repaint within the bar.',
};

const DEFAULT_PAYLOAD = {
  signal_trigger_mode: 'disabled',
  max_intrabar_signal_age_ms: 2500,
  intrabar_throttle_ms: 750,
  risk_event_throttle_ms: 250,
  same_bar_reversal_enabled: false,
  duplicate_same_bar_policy: 'first_wins',
  bar_close_max_signal_age_seconds: 90,
  practice_only_guard: true,
  funded_account_allowed: false,
  // Persistent entry global defaults (global scope only)
  timing_allow_persistent_signal_entry: false,
  timing_persistent_entry_max_age_bars: 6,
  timing_persistent_entry_min_state_bars: 2,
  timing_persistent_entry_cooldown_after_close_bars: 3,
  timing_persistent_entry_max_atr_distance_from_kama: 1.5,
  timing_require_broker_flat: true,
  timing_require_broker_state_confirmed: true,
};

const PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ModeBadge({ mode }) {
  if (!mode) return null;
  const tone =
    mode === 'intrabar'
      ? 'bg-amber-500/15 text-amber-200 border-amber-500/30'
      : mode === 'bar_close'
      ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30'
      : 'bg-zinc-700/40 text-zinc-300 border-zinc-500/30';
  return (
    <Badge className={`${tone} border uppercase tracking-wider text-[10px]`}>
      {SIGNAL_MODE_LABEL[mode] || mode}
    </Badge>
  );
}

function SourceBadge({ source }) {
  if (!source) return null;
  const isOverride = source === 'account' || source === 'link';
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-medium ${
        isOverride
          ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
          : 'bg-zinc-700/30 text-zinc-500 border border-zinc-600/30'
      }`}
    >
      {isOverride ? 'Override' : 'Global'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// TimingForm — full editor used in global card + modal
// ---------------------------------------------------------------------------

function TimingForm({ initial, onSave, onClear, allowClear, scopeLabel, accountIsFunded }) {
  const [draft, setDraft] = useState({ ...DEFAULT_PAYLOAD, ...initial });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft({ ...DEFAULT_PAYLOAD, ...initial });
  }, [initial]);

  const fundedRiskInPlay = useMemo(
    () => accountIsFunded && draft.signal_trigger_mode !== 'disabled',
    [accountIsFunded, draft.signal_trigger_mode]
  );

  const requiresGuardOverride = useMemo(
    () =>
      fundedRiskInPlay &&
      (draft.practice_only_guard || !draft.funded_account_allowed),
    [fundedRiskInPlay, draft.practice_only_guard, draft.funded_account_allowed]
  );

  const handleNumber = (key) => (event) => {
    const value = Number(event.target.value);
    setDraft((prev) => ({
      ...prev,
      [key]: Number.isFinite(value) ? Math.max(0, Math.floor(value)) : prev[key],
    }));
  };

  const handleSave = async () => {
    if (fundedRiskInPlay) {
      setConfirmOpen(true);
      return;
    }
    await persist();
  };

  const persist = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      toast.success(`Saved ${scopeLabel}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Save failed';
      toast.error(detail);
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-wider text-white/55">Signal trigger mode</div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.keys(SIGNAL_MODE_LABEL).map((mode) => {
            const selected = draft.signal_trigger_mode === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setDraft((prev) => ({ ...prev, signal_trigger_mode: mode }))}
                className={`text-left rounded border px-3 py-2 transition ${
                  selected
                    ? 'border-blue-500/50 bg-blue-500/15 text-white'
                    : 'border-white/10 bg-black/20 text-white/75 hover:bg-black/30'
                }`}
              >
                <div className="text-sm font-semibold">{SIGNAL_MODE_LABEL[mode]}</div>
                <div className="mt-1 text-[11px] text-white/60">
                  {SIGNAL_MODE_DESCRIPTION[mode]}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs text-white/65">
          <span>Max intrabar signal age (ms)</span>
          <Input
            type="number"
            value={draft.max_intrabar_signal_age_ms}
            onChange={handleNumber('max_intrabar_signal_age_ms')}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-white/65">
          <span>Intrabar throttle (ms)</span>
          <Input
            type="number"
            value={draft.intrabar_throttle_ms}
            onChange={handleNumber('intrabar_throttle_ms')}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-white/65">
          <span>Risk event throttle (ms)</span>
          <Input
            type="number"
            value={draft.risk_event_throttle_ms}
            onChange={handleNumber('risk_event_throttle_ms')}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-white/65">
          <span>Bar-close max signal age (s)</span>
          <Input
            type="number"
            value={draft.bar_close_max_signal_age_seconds}
            onChange={handleNumber('bar_close_max_signal_age_seconds')}
          />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs text-white/65">
          <span>Duplicate same-bar policy</span>
          <Select
            value={draft.duplicate_same_bar_policy}
            onValueChange={(value) =>
              setDraft((prev) => ({ ...prev, duplicate_same_bar_policy: value }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="first_wins">First wins (drop later)</SelectItem>
              <SelectItem value="reject">Reject duplicates</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="flex items-center gap-3 rounded border border-white/10 bg-black/20 px-3 py-2">
          <Switch
            checked={draft.same_bar_reversal_enabled}
            onCheckedChange={(checked) =>
              setDraft((prev) => ({ ...prev, same_bar_reversal_enabled: !!checked }))
            }
          />
          <span className="text-xs text-white/70">Allow same-bar reversal</span>
        </label>
        <label className="flex items-center gap-3 rounded border border-white/10 bg-black/20 px-3 py-2">
          <Switch
            checked={draft.practice_only_guard}
            onCheckedChange={(checked) =>
              setDraft((prev) => ({ ...prev, practice_only_guard: !!checked }))
            }
          />
          <span className="text-xs text-white/70">Practice-only guard</span>
        </label>
        <label className="flex items-center gap-3 rounded border border-white/10 bg-black/20 px-3 py-2">
          <Switch
            checked={draft.funded_account_allowed}
            onCheckedChange={(checked) =>
              setDraft((prev) => ({ ...prev, funded_account_allowed: !!checked }))
            }
          />
          <span className="text-xs text-white/70">Funded account allowed</span>
        </label>
      </div>

      {/* Persistent entry global defaults — shown only for the global scope row */}
      {scopeLabel === 'global default' && (
        <div className="border-t border-white/10 pt-4 space-y-3">
          <div className="text-xs uppercase tracking-wider text-white/55">Persistent Entry — Global Default</div>
          <p className="text-[11px] text-white/45">
            Sets the system-wide default for persistent/resume entry. Users can override these in their own Settings; per-link overrides take highest priority.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center gap-3 rounded border border-white/10 bg-black/20 px-3 py-2">
              <Switch
                checked={draft.timing_allow_persistent_signal_entry}
                onCheckedChange={(checked) =>
                  setDraft((prev) => ({ ...prev, timing_allow_persistent_signal_entry: !!checked }))
                }
              />
              <span className="text-xs text-white/70">Allow persistent entry</span>
            </label>
            <label className="flex items-center gap-3 rounded border border-white/10 bg-black/20 px-3 py-2">
              <Switch
                checked={draft.timing_require_broker_flat}
                onCheckedChange={(checked) =>
                  setDraft((prev) => ({ ...prev, timing_require_broker_flat: !!checked }))
                }
              />
              <span className="text-xs text-white/70">Require broker flat</span>
            </label>
            <label className="flex items-center gap-3 rounded border border-white/10 bg-black/20 px-3 py-2">
              <Switch
                checked={draft.timing_require_broker_state_confirmed}
                onCheckedChange={(checked) =>
                  setDraft((prev) => ({ ...prev, timing_require_broker_state_confirmed: !!checked }))
                }
              />
              <span className="text-xs text-white/70">Require broker state confirmed</span>
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="flex flex-col gap-1 text-xs text-white/65">
              <span>Max age (bars)</span>
              <Input
                type="number"
                min={1}
                value={draft.timing_persistent_entry_max_age_bars}
                onChange={(e) => setDraft((prev) => ({ ...prev, timing_persistent_entry_max_age_bars: Math.max(1, parseInt(e.target.value || '6', 10) || 6) }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-white/65">
              <span>Min state bars</span>
              <Input
                type="number"
                min={1}
                value={draft.timing_persistent_entry_min_state_bars}
                onChange={(e) => setDraft((prev) => ({ ...prev, timing_persistent_entry_min_state_bars: Math.max(1, parseInt(e.target.value || '2', 10) || 2) }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-white/65">
              <span>Cooldown bars</span>
              <Input
                type="number"
                min={0}
                value={draft.timing_persistent_entry_cooldown_after_close_bars}
                onChange={(e) => setDraft((prev) => ({ ...prev, timing_persistent_entry_cooldown_after_close_bars: Math.max(0, parseInt(e.target.value || '3', 10) || 0) }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-white/65">
              <span>Max ATR distance</span>
              <Input
                type="number"
                step={0.1}
                min={0.1}
                value={draft.timing_persistent_entry_max_atr_distance_from_kama}
                onChange={(e) => setDraft((prev) => ({ ...prev, timing_persistent_entry_max_atr_distance_from_kama: Math.max(0.1, parseFloat(e.target.value || '1.5') || 1.5) }))}
              />
            </label>
          </div>
        </div>
      )}

      {fundedRiskInPlay && (
        <div className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            This account is not flagged as practice. Saving will require both
            <span className="font-mono"> practice_only_guard=false </span>and
            <span className="font-mono"> funded_account_allowed=true</span>. A
            confirmation modal will appear before the change is saved.
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="default"
          onClick={handleSave}
          disabled={saving || (fundedRiskInPlay && requiresGuardOverride)}
        >
          <Save className="w-4 h-4 mr-1" />
          Save {scopeLabel}
        </Button>
        {allowClear && onClear && (
          <Button variant="outline" onClick={onClear} disabled={saving}>
            Clear override
          </Button>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm funded-account automation</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-white/80">
            <p>
              You are about to enable
              <span className="mx-1 font-mono">{draft.signal_trigger_mode}</span>
              automation on a non-practice account. This is a high-risk action.
            </p>
            <p>I understand this allows automation on a funded account.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={persist} disabled={saving}>
              I understand — save anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AccountOverridesTable — searchable, paginated, lazy-loading timing editor
// ---------------------------------------------------------------------------

function AccountOverridesTable({ accounts, onSave, onClear, onRequestTimingLoad, accountTimings }) {
  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState('all');   // all | disabled | bar_close | intrabar | override
  const [typeFilter, setTypeFilter] = useState('all');   // all | funded | practice
  const [page, setPage] = useState(1);
  const [editAccount, setEditAccount] = useState(null);  // account object being edited
  const searchRef = useRef(null);

  // Filter accounts client-side
  const filtered = useMemo(() => {
    let result = accounts;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (a) =>
          (a.account_name || '').toLowerCase().includes(q) ||
          (a.id || '').toLowerCase().includes(q)
      );
    }

    if (typeFilter === 'funded') {
      result = result.filter(
        (a) => !['sim', 'practice'].includes(String(a.account_type || '').toLowerCase())
      );
    } else if (typeFilter === 'practice') {
      result = result.filter((a) =>
        ['sim', 'practice'].includes(String(a.account_type || '').toLowerCase())
      );
    }

    if (modeFilter === 'override') {
      result = result.filter((a) => accountTimings[a.id]?.row?.id);
    } else if (modeFilter !== 'all') {
      result = result.filter(
        (a) =>
          (accountTimings[a.id]?.effective?.signal_trigger_mode || 'disabled') === modeFilter
      );
    }

    return result;
  }, [accounts, search, modeFilter, typeFilter, accountTimings]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageAccounts = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, modeFilter, typeFilter]);

  // Lazy-load timing for accounts that appear in the current page
  useEffect(() => {
    pageAccounts.forEach((a) => {
      if (!accountTimings[a.id]) onRequestTimingLoad(a.id);
    });
  }, [pageAccounts, accountTimings, onRequestTimingLoad]);

  // Count overrides
  const overrideCount = useMemo(
    () => accounts.filter((a) => accountTimings[a.id]?.row?.id).length,
    [accounts, accountTimings]
  );

  const fundedCount = useMemo(
    () =>
      accounts.filter(
        (a) => !['sim', 'practice'].includes(String(a.account_type || '').toLowerCase())
      ).length,
    [accounts]
  );

  const handleEdit = (acct) => {
    // Ensure timing is loaded before opening modal
    if (!accountTimings[acct.id]) onRequestTimingLoad(acct.id);
    setEditAccount(acct);
  };

  const handleModalSave = async (draft) => {
    await onSave(editAccount.id)(draft);
    setEditAccount(null);
  };

  const handleModalClear = async () => {
    await onClear(editAccount.id)();
    setEditAccount(null);
  };

  const editTiming = editAccount ? (accountTimings[editAccount.id] || { row: {}, effective: null }) : null;
  const editIsFunded = editAccount
    ? !['sim', 'practice'].includes(String(editAccount.account_type || '').toLowerCase())
    : false;

  return (
    <>
      {/* Stats bar */}
      <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px] text-white/45">
        <span>{accounts.length.toLocaleString()} accounts</span>
        <span className="text-white/20">·</span>
        <span className="text-blue-300/70">{overrideCount} with overrides</span>
        <span className="text-white/20">·</span>
        <span className="text-red-300/70">{fundedCount} funded</span>
        {filtered.length !== accounts.length && (
          <>
            <span className="text-white/20">·</span>
            <span className="text-amber-300/70">{filtered.length} matching filter</span>
          </>
        )}
      </div>

      {/* Search + filter toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/35 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search by name or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-8 py-1.5 rounded border border-white/10 bg-black/30 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/35 hover:text-white/70"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Mode filter chips */}
        <div className="flex items-center gap-1 flex-wrap">
          {[
            { key: 'all', label: 'All modes' },
            { key: 'disabled', label: 'Disabled' },
            { key: 'bar_close', label: 'Bar Close' },
            { key: 'intrabar', label: 'Intrabar' },
            { key: 'override', label: 'Has override' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setModeFilter(key)}
              className={`px-2 py-1 rounded text-[11px] border transition ${
                modeFilter === key
                  ? 'bg-blue-500/20 border-blue-500/40 text-blue-200'
                  : 'border-white/10 text-white/45 hover:border-white/25 hover:text-white/70'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'funded', label: '🔴 Funded' },
            { key: 'practice', label: 'Sim/Practice' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTypeFilter(key)}
              className={`px-2 py-1 rounded text-[11px] border transition ${
                typeFilter === key
                  ? 'bg-zinc-600/40 border-zinc-400/40 text-white'
                  : 'border-white/10 text-white/45 hover:border-white/25 hover:text-white/70'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-white/40">
          {accounts.length === 0
            ? 'No accounts discoverable yet.'
            : 'No accounts match the current filter.'}
        </div>
      ) : (
        <>
          <div className="rounded border border-white/10 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 bg-white/5">
                  <TableHead className="text-[11px] text-white/50 py-2">Account</TableHead>
                  <TableHead className="text-[11px] text-white/50 py-2">Type</TableHead>
                  <TableHead className="text-[11px] text-white/50 py-2">Effective mode</TableHead>
                  <TableHead className="text-[11px] text-white/50 py-2">Source</TableHead>
                  <TableHead className="text-[11px] text-white/50 py-2 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageAccounts.map((acct) => {
                  const timing = accountTimings[acct.id];
                  const effectiveMode = timing?.effective?.signal_trigger_mode || null;
                  const source = timing?.effective?.source || null;
                  const hasOverride = Boolean(timing?.row?.id);
                  const isFunded = !['sim', 'practice'].includes(
                    String(acct.account_type || '').toLowerCase()
                  );

                  return (
                    <TableRow
                      key={acct.id}
                      className="border-white/5 hover:bg-white/3 transition-colors"
                    >
                      <TableCell className="py-2">
                        <div className="text-sm text-white leading-tight">
                          {acct.account_name || acct.id}
                        </div>
                        <div className="text-[10px] text-white/35 font-mono mt-0.5 truncate max-w-[200px]">
                          {acct.id}
                        </div>
                      </TableCell>

                      <TableCell className="py-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-white/55 capitalize">
                            {acct.account_type || 'unknown'}
                          </span>
                          {isFunded && (
                            <span className="inline-flex items-center gap-0.5 rounded border border-red-500/40 bg-red-500/15 px-1 py-0.5 text-[9px] uppercase text-red-200">
                              <AlertTriangle className="w-2.5 h-2.5" />
                              Funded
                            </span>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="py-2">
                        {timing === undefined ? (
                          <span className="text-[10px] text-white/25 italic">Loading…</span>
                        ) : effectiveMode ? (
                          <ModeBadge mode={effectiveMode} />
                        ) : (
                          <span className="text-[10px] text-white/25">—</span>
                        )}
                      </TableCell>

                      <TableCell className="py-2">
                        {timing === undefined ? null : <SourceBadge source={source} />}
                      </TableCell>

                      <TableCell className="py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-white/50 hover:text-white"
                            title={hasOverride ? 'Edit override' : 'Add override'}
                            onClick={() => handleEdit(acct)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          {hasOverride && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-400/60 hover:text-red-400"
                              title="Clear override"
                              onClick={() => onClear(acct.id)()}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-[11px] text-white/45">
              <span>
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of{' '}
                {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                {/* Page number pills — show at most 7 */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === totalPages ||
                      Math.abs(p - safePage) <= 2
                  )
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '…' ? (
                      <span key={`ellipsis-${i}`} className="px-1 text-white/25">
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`h-6 min-w-[24px] px-1.5 rounded text-[11px] transition ${
                          p === safePage
                            ? 'bg-blue-500/25 text-blue-200 border border-blue-500/40'
                            : 'text-white/45 hover:text-white/70 hover:bg-white/5'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit modal */}
      <Dialog open={Boolean(editAccount)} onOpenChange={(open) => !open && setEditAccount(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Pencil className="w-4 h-4 text-white/50" />
              <span>
                {editAccount?.account_name || editAccount?.id}
                {editIsFunded && (
                  <span className="ml-2 inline-flex items-center gap-0.5 rounded border border-red-500/40 bg-red-500/15 px-1.5 py-0.5 text-[10px] uppercase text-red-200">
                    <AlertTriangle className="w-3 h-3" />
                    Funded
                  </span>
                )}
              </span>
            </DialogTitle>
            {editAccount && (
              <p className="text-[11px] text-white/40 font-mono mt-0.5">{editAccount.id}</p>
            )}
          </DialogHeader>
          {editAccount && editTiming && (
            <div className="mt-2">
              {editTiming === undefined ? (
                <div className="py-8 text-center text-sm text-white/40">Loading timing…</div>
              ) : (
                <TimingForm
                  initial={editTiming.row}
                  onSave={handleModalSave}
                  onClear={handleModalClear}
                  scopeLabel={`override for ${editAccount.account_name || editAccount.id}`}
                  allowClear={Boolean(editTiming.row?.id)}
                  accountIsFunded={editIsFunded}
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

const AutomationTimingTab = () => {
  const [globalRow, setGlobalRow] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountTimings, setAccountTimings] = useState({}); // account_id -> { row, effective } | null (loading)
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState([]);
  const loadingIds = useRef(new Set());

  // Fetch a single account's timing on demand
  const loadAccountTiming = useCallback(async (accountId) => {
    if (loadingIds.current.has(accountId)) return;
    loadingIds.current.add(accountId);
    try {
      const res = await getAutomationTiming('account', accountId);
      setAccountTimings((prev) => ({ ...prev, [accountId]: res.data }));
    } catch {
      setAccountTimings((prev) => ({ ...prev, [accountId]: { row: {}, effective: null } }));
    } finally {
      loadingIds.current.delete(accountId);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const globalRes = await getAutomationTiming('global');
      setGlobalRow(globalRes.data);

      let accountsList = [];
      try {
        const acctRes = await api.get('/accounts');
        accountsList = Array.isArray(acctRes.data?.accounts) ? acctRes.data.accounts : [];
      } catch {
        accountsList = [];
      }
      setAccounts(accountsList);
      // Reset timing cache so page-0 accounts will be lazily re-fetched
      setAccountTimings({});
      loadingIds.current.clear();

      try {
        const auditRes = await api.get('/strategies/scope-audit', {
          params: { event_type: 'automation_timing_change', limit: 20 },
        });
        setAudit(Array.isArray(auditRes.data?.events) ? auditRes.data.events : []);
      } catch {
        setAudit([]);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSaveGlobal = useCallback(
    async (draft) => {
      await putAutomationTiming({ ...draft, scope_type: 'global', scope_id: null });
      await refresh();
    },
    [refresh]
  );

  const handleSaveAccount = useCallback(
    (accountId) => async (draft) => {
      await putAutomationTiming({ ...draft, scope_type: 'account', scope_id: accountId });
      // Refresh only this account's timing
      loadingIds.current.delete(accountId);
      await loadAccountTiming(accountId);
    },
    [loadAccountTiming]
  );

  const handleClearAccount = useCallback(
    (accountId) => async () => {
      try {
        await deleteAutomationTiming('account', accountId);
        toast.success('Override cleared');
        loadingIds.current.delete(accountId);
        await loadAccountTiming(accountId);
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'Failed to clear';
        toast.error(detail);
      }
    },
    [loadAccountTiming]
  );

  return (
    <div className="space-y-4">
      {/* Global default */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4" />
            Global automation timing default
          </CardTitle>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {globalRow ? (
            <TimingForm
              initial={globalRow.row}
              onSave={handleSaveGlobal}
              scopeLabel="global default"
              allowClear={false}
              accountIsFunded={false}
            />
          ) : (
            <div className="text-sm text-white/55">Loading…</div>
          )}
        </CardContent>
      </Card>

      {/* Per-account overrides — searchable table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            Per-account overrides
          </CardTitle>
          <p className="text-[11px] text-white/40 mt-1">
            Overrides are applied on top of the global default. Use the pencil icon to set or edit an account's timing. Accounts without an override inherit the global default.
          </p>
        </CardHeader>
        <CardContent>
          <AccountOverridesTable
            accounts={accounts}
            accountTimings={accountTimings}
            onSave={handleSaveAccount}
            onClear={handleClearAccount}
            onRequestTimingLoad={loadAccountTiming}
          />
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Recent timing changes</CardTitle>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <div className="text-sm text-white/55">No recorded timing changes.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Updated by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.map((row) => {
                  const details = (() => {
                    try {
                      return typeof row.details === 'string'
                        ? JSON.parse(row.details)
                        : row.details || {};
                    } catch {
                      return {};
                    }
                  })();
                  const scope = `${details.scope_type || ''}${
                    details.scope_id ? `:${details.scope_id}` : ''
                  }`;
                  const mode = details?.new?.signal_trigger_mode || '';
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="text-xs">
                        {row.created_at
                          ? new Date(row.created_at).toLocaleString()
                          : ''}
                      </TableCell>
                      <TableCell className="text-xs">{scope}</TableCell>
                      <TableCell>
                        <ModeBadge mode={mode} />
                      </TableCell>
                      <TableCell className="text-xs">
                        {details.updated_by || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AutomationTimingTab;
